import { TabList, Tabs, tabClasses } from '@mui/joy'
import { forwardRef, useRef, useState } from 'react'

interface ScrollableTabsProps {
    value: string | number
    onChange: (event: React.SyntheticEvent | null, value: string | number) => void
    children: React.ReactNode
    sx?: any
}

interface ScrollableTabListProps {
    children: React.ReactNode
    sx?: any
}

export const ScrollableTabList = forwardRef<HTMLDivElement, ScrollableTabListProps>(
    ({ children, sx }, ref) => {
        const [isMouseDown, setIsMouseDown] = useState(false)
        const [startX, setStartX] = useState(0)
        const [scrollLeft, setScrollLeft] = useState(0)
        const [isDragging, setIsDragging] = useState(false)
        const tabListRef = useRef<HTMLDivElement>(null)

        const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
            setIsMouseDown(true)
            setIsDragging(false)
            setStartX(e.pageX - e.currentTarget.offsetLeft)
            setScrollLeft(e.currentTarget.scrollLeft)
        }

        const handleMouseLeave = () => setIsMouseDown(false)
        const handleMouseUp = () => setIsMouseDown(false)

        const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
            if (!isMouseDown) return
            e.preventDefault()
            const x = e.pageX - e.currentTarget.offsetLeft
            const walk = x - startX
            if (Math.abs(walk) > 5) {
                setIsDragging(true)
            }
            e.currentTarget.scrollLeft = scrollLeft - walk
        }

        return (
            <TabList
                ref={ref}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onClick={e => {
                    if (isDragging) {
                        e.preventDefault()
                    }
                }}
                sx={{
                    cursor: 'grab',
                    px: {
                        xs: 2,
                        sm: 4,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                    },
                    [`& .${tabClasses.root}`]: {
                        flex: 'none',
                    },
                    ...sx,
                }}
            >
                {children}
            </TabList>
        )
    },
)

export const ScrollableTabs = ({ value, onChange, children, sx }: ScrollableTabsProps) => {
    const [isDragging, setIsDragging] = useState(false)

    return (
        <Tabs
            value={value}
            onChange={(e, val) => {
                if (isDragging) {
                    e?.preventDefault()
                    e?.stopPropagation()
                    return
                }
                if (val !== null) {
                    onChange(e, val)
                }
            }}
            sx={{ mx: 0, ...sx }}
        >
            {children}
        </Tabs>
    )
}
